import { forwardRef, useState, useEffect } from "react";

interface BackgroundImageProps {
  imageUrl: string;
  type?: "cover" | "contain" | "fixed-aspect" | "fill-container";
  width?: any;
  height?: any;
  aspectRatio?: number;
}

const withBackgroundImage = <P extends object>(Component: React.ComponentType<P>) => {
  return forwardRef<any, React.PropsWithChildren<P & BackgroundImageProps>>(
    ({ children, imageUrl, ...props }, ref) => {
      const { sx, width = 0, height = 0, type = "cover", aspectRatio, ...rest } = props as any;

      const [dimensions, setDimensions] = useState({ width, height });
      const [naturalAspectRatio, setNaturalAspectRatio] = useState<number | null>(null);

      useEffect(() => {
        let tempWidth = width ?? 0;
        let tempHeight = height ?? 0;

        // Load image to get natural dimensions and aspect ratio
        const img = new Image();
        img.src = imageUrl;
        img.onload = () => {
          const naturalRatio = img.naturalWidth / img.naturalHeight;
          setNaturalAspectRatio(naturalRatio);

          if (type === "fixed-aspect") {
            // Type 1: Keep aspect ratio, max-width 100%, width unchanged
            const finalAspectRatio = aspectRatio || naturalRatio;

            if (tempWidth && !tempHeight) {
              // Width provided, calculate height from aspect ratio
              setDimensions({ width: tempWidth, height: tempWidth / finalAspectRatio });
            } else if (!tempWidth && tempHeight) {
              // Height provided, calculate width from aspect ratio
              setDimensions({ width: tempHeight * finalAspectRatio, height: tempHeight });
            } else if (tempWidth && tempHeight) {
              // Both provided, use width and calculate height to maintain aspect ratio
              setDimensions({ width: tempWidth, height: tempWidth / finalAspectRatio });
            } else {
              // Neither provided, use natural dimensions
              setDimensions({ width: img.naturalWidth, height: img.naturalHeight });
            }
          } else if (type === "fill-container") {
            // Type 2: Follow container size, ignore aspect ratio
            if (tempWidth && tempHeight) {
              setDimensions({ width: tempWidth, height: tempHeight });
            } else if (tempWidth && !tempHeight) {
              setDimensions({ width: tempWidth, height: tempWidth });
            } else if (!tempWidth && tempHeight) {
              setDimensions({ width: tempHeight, height: tempHeight });
            } else {
              // Neither provided, use natural dimensions as fallback
              setDimensions({ width: img.naturalWidth, height: img.naturalHeight });
            }
          } else {
            // Original logic for "cover" and "contain" types
            if (aspectRatio && (tempWidth || tempHeight) && (!tempWidth || !tempHeight)) {
              if (!tempWidth) {
                tempWidth = tempHeight * aspectRatio;
              } else if (!tempHeight) {
                tempHeight = tempWidth / aspectRatio;
              }
              setDimensions({ width: tempWidth, height: tempHeight });
            } else if ((!tempWidth || !tempHeight) && !aspectRatio) {
              if (!width && !height) {
                setDimensions({ width: img.naturalWidth, height: img.naturalHeight });
              } else if (!width && height) {
                setDimensions({ width: img.naturalWidth, height });
              } else {
                setDimensions({ width, height: img.naturalHeight });
              }
            }
          }
        };
      }, [imageUrl, width, height, aspectRatio, type]);

      // Calculate styles based on type
      const getBackgroundStyles = () => {
        const baseStyles = {
          backgroundImage: `url(${imageUrl})`,
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
        };

        switch (type) {
          case "fixed-aspect":
            return {
              ...baseStyles,
              backgroundSize: "cover",
              width: dimensions.width,
              height: dimensions.height,
              maxWidth: "100%",
              // Maintain aspect ratio when container shrinks
              aspectRatio: naturalAspectRatio || aspectRatio || "auto",
            };

          case "fill-container":
            return {
              ...baseStyles,
              backgroundSize: "cover",
              width: dimensions.width,
              height: dimensions.height,
              // No max constraints, fill the specified dimensions
            };

          case "contain":
            return {
              ...baseStyles,
              backgroundSize: "contain",
              width: dimensions.width,
              height: dimensions.height,
              maxWidth: "100%",
              maxHeight: "100%",
            };

          case "cover":
          default:
            return {
              ...baseStyles,
              backgroundSize: "cover",
              width: dimensions.width,
              height: dimensions.height,
              maxWidth: "100%",
              maxHeight: "100%",
            };
        }
      };

      return (
        <Component
          ref={ref}
          sx={{
            ...getBackgroundStyles(),
            ...sx,
          }}
          {...(rest as P)}>
          {children}
        </Component>
      );
    },
  );
};

export { withBackgroundImage };
